import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUserProfile } from '@busy/api';
import { Avatar } from '@busy/ui';
import { useAuth } from '../store/auth';
import { useUI } from '../store/ui';
import { useT } from '../lib/i18n';

// Top-right avatar button + dropdown menu. Owns the link to Settings and a quick
// theme toggle so the user can flip light/dark from anywhere without diving into
// the full Settings tab.
export function AvatarMenu() {
  const navigate = useNavigate();
  const profile = useCurrentUserProfile();
  const user = useAuth((s) => s.user);
  const signOut = useAuth((s) => s.signOut);
  const theme = useUI((s) => s.theme);
  const setTheme = useUI((s) => s.setTheme);
  const language = useUI((s) => s.language);
  const setLanguage = useUI((s) => s.setLanguage);
  const t = useT();

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside + Escape close
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayName = profile.data
    ? `${profile.data.first_name ?? ''} ${profile.data.last_name ?? ''}`.trim() ||
      profile.data.email
    : user?.email ?? t('menu.you');

  function go(to: string) {
    setOpen(false);
    navigate(to);
  }

  async function handleSignOut() {
    setOpen(false);
    if (!confirm(t('menu.signOutConfirm'))) return;
    await signOut();
    navigate('/sign-in', { replace: true });
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('menu.aria')}
        aria-expanded={open}
        className="rounded-full focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
      >
        <Avatar name={displayName} size="md" />
      </button>

      {open && (
        // Reset text color explicitly so we don't inherit `text-white` from the
        // navy topbar that hosts this menu — that's what made the dropdown
        // unreadable in light mode (white-on-surface).
        <div className="absolute right-0 top-12 w-64 rounded-xl bg-surface text-ink-900 border border-line shadow-xl z-40 overflow-hidden">
          <div className="px-4 py-3 border-b border-line">
            <div className="text-sm font-semibold text-ink-900 truncate">{displayName}</div>
            {profile.data?.email && profile.data.email !== displayName && (
              <div className="text-xs text-ink-500 truncate">{profile.data.email}</div>
            )}
          </div>

          <MenuItem icon={<TrophyIcon />} onClick={() => go('/achievements')}>
            {t('ach.gallery.menuLink')}
          </MenuItem>

          <MenuItem icon={<SettingsIcon />} onClick={() => go('/settings')}>
            {t('menu.settings')}
          </MenuItem>

          <button
            type="button"
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-bg-alt text-left text-sm text-ink-900"
          >
            {theme === 'light' ? <MoonIcon /> : <SunIcon />}
            <span className="flex-1">
              {theme === 'light' ? t('menu.darkMode') : t('menu.lightMode')}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-bg-alt text-left text-sm text-ink-900"
          >
            <GlobeIcon />
            <span className="flex-1">{t('menu.languageToggle')}</span>
            <span className="text-xs text-ink-500 font-mono uppercase">
              {language === 'en' ? 'EN → ES' : 'ES → EN'}
            </span>
          </button>

          <div className="border-t border-line">
            <MenuItem icon={<LogOutIcon />} onClick={handleSignOut} kind="danger">
              {t('menu.signOut')}
            </MenuItem>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  onClick,
  kind = 'default',
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  kind?: 'default' | 'danger';
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-bg-alt text-left text-sm ${
        kind === 'danger' ? 'text-red-600 dark:text-red-400' : 'text-ink-900'
      }`}
    >
      {icon}
      <span className="flex-1">{children}</span>
    </button>
  );
}

function TrophyIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}
function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
function GlobeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}
function LogOutIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
